/**
 * REGLAS DE LA MAQUINARIA DE APLICACIÓN (F2 del plan del cuaderno, 26-09-2026).
 *
 * COPIA DE CLIENTE de `supabase/functions/_shared/equipmentRules.ts`: la
 * pantalla calcula lo mismo que el servidor (el régimen, la próxima inspección
 * y qué máquina saldrá en el parte). `equipmentRules.parity.test.ts` compara las
 * dos caso a caso: si se toca una, se toca la otra.
 *
 * Qué decide:
 *  - el RÉGIMEN de cada tipo cerrado de máquina (los de la plantilla de SAT
 *    Inver y la norma): inscrita en el ROMA, en el censo autonómico o exenta;
 *  - la PRÓXIMA inspección, calculada y no escrita a mano: la primera antes de
 *    cumplir 5 años desde la compra y después cada 3 (RD 1702/2011). Solo para
 *    los tipos sujetos; una mochila no se inspecciona y no lleva avisos falsos;
 *  - QUÉ máquina va en un parte cuando nadie la elige (`pickEquipment`);
 *  - la COPIA que se guarda en el parte (`equipmentSnapshot`).
 *
 * Las fechas son 'AAAA-MM-DD' y se suman como texto: nada de zonas horarias.
 */

export const EQUIPMENT_TYPES = [
  "pulverizador",
  "atomizador",
  "canon",
  "espolvoreadora",
  "fertirrigacion",
  "instalacion_fija",
  "mochila",
  "otro",
] as const;
export type EquipmentType = (typeof EQUIPMENT_TYPES)[number];

/** Dónde se inscribe: ROMA, censo de la comunidad autónoma, o ninguno. */
export type EquipmentRegime = "roma" | "censo" | "exenta";

const REGIME: Record<EquipmentType, EquipmentRegime | null> = {
  pulverizador: "roma", // cuba o carretilla de más de 100 L
  atomizador: "roma",
  canon: "roma",
  espolvoreadora: "roma",
  fertirrigacion: "censo", // aplicación por el riego
  instalacion_fija: "censo", // instalación fija de invernadero
  mochila: "exenta", // mochila o carretilla de menos de 100 L
  otro: null, // no se sabe: se piden los dos números y no se avisa de nada
};

export function isEquipmentType(v: unknown): v is EquipmentType {
  return typeof v === "string" && (EQUIPMENT_TYPES as readonly string[]).includes(v);
}

export function equipmentRegime(type: string | null | undefined): EquipmentRegime | null {
  return isEquipmentType(type) ? REGIME[type] : null;
}

/** ¿Pasa inspecciones periódicas? Las del ROMA y las del censo, sí. */
export function isInspectionSubject(type: string | null | undefined): boolean {
  const r = equipmentRegime(type);
  return r === "roma" || r === "censo";
}

/** Primera inspección: antes de 5 años desde la compra. Luego, cada 3. */
export const FIRST_INSPECTION_YEARS = 5;
export const INSPECTION_EVERY_YEARS = 3;

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})/;

/** 'AAAA-MM-DD' + N años, sin pasar por Date (el 29-F cae en el 28). */
export function addYears(isoDate: string | null | undefined, years: number): string | null {
  const m = ISO_DATE.exec(isoDate ?? "");
  if (!m) return null;
  const y = Number(m[1]) + years;
  const mo = m[2];
  let d = m[3];
  if (mo === "02" && d === "29") {
    const leap = (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
    if (!leap) d = "28";
  }
  return `${String(y).padStart(4, "0")}-${mo}-${d}`;
}

export interface EquipmentDates {
  equipment_type: string | null | undefined;
  acquired_on?: string | null;
  last_inspection_on?: string | null;
}

/**
 * Próxima inspección. Con inspección hecha: esa + 3 años. Sin ninguna: compra + 5.
 * Sin fechas, o si el tipo no se inspecciona: null (no se sabe, no se inventa).
 */
export function nextInspectionOn(e: EquipmentDates): string | null {
  if (!isInspectionSubject(e.equipment_type)) return null;
  if (e.last_inspection_on) return addYears(e.last_inspection_on, INSPECTION_EVERY_YEARS);
  if (e.acquired_on) return addYears(e.acquired_on, FIRST_INSPECTION_YEARS);
  return null;
}

/** Días que se avisa antes de que venza la inspección. */
export const INSPECTION_WARN_DAYS = 60;

/** Días entre dos fechas 'AAAA-MM-DD' (b − a). */
export function daysBetween(a: string, b: string): number | null {
  const ma = ISO_DATE.exec(a);
  const mb = ISO_DATE.exec(b);
  if (!ma || !mb) return null;
  const ta = Date.UTC(Number(ma[1]), Number(ma[2]) - 1, Number(ma[3]));
  const tb = Date.UTC(Number(mb[1]), Number(mb[2]) - 1, Number(mb[3]));
  return Math.round((tb - ta) / 86_400_000);
}

export type InspectionStatus = "ok" | "soon" | "overdue" | "unknown";

/**
 * Cómo está la inspección HOY. `null` para los tipos que no se inspeccionan: una
 * mochila no tiene nada que avisar. `unknown` = sujeta pero sin fechas.
 */
export function inspectionStatus(e: EquipmentDates, today: string): InspectionStatus | null {
  if (!isInspectionSubject(e.equipment_type)) return null;
  const next = nextInspectionOn(e);
  if (!next) return "unknown";
  const left = daysBetween(today, next);
  if (left == null) return "unknown";
  if (left < 0) return "overdue";
  if (left <= INSPECTION_WARN_DAYS) return "soon";
  return "ok";
}

/** Lo que hace falta de una máquina para elegirla y copiarla en el parte. */
export interface EquipmentRow {
  id: string;
  location_id: string | null;
  equipment_type: string;
  description: string;
  brand?: string | null;
  model?: string | null;
  roma_number?: string | null;
  census_reference?: string | null;
  acquired_on?: string | null;
  last_inspection_on?: string | null;
  is_default: boolean;
  active: boolean;
  archived_at?: string | null;
}

const usable = (e: EquipmentRow) => e.active && !e.archived_at;

/**
 * QUÉ MÁQUINA VA EN EL PARTE, en este orden (D6):
 *   1. la elegida en la tarea («⋯» → «Maquinaria…»), si sigue activa;
 *   2. la de por defecto de la finca de la nave;
 *   3. la de por defecto de la empresa (sin finca);
 *   4. la ÚNICA activa que se puede usar ahí (de esa finca o de toda la empresa);
 *   5. ninguna: el parte sale sin máquina y el cuaderno lo enseña como hueco.
 * Una máquina archivada después de planificar no se usa: se cae a la siguiente.
 */
export function pickEquipment<T extends EquipmentRow>(
  list: readonly T[],
  opts: { chosenId?: string | null; locationId?: string | null },
): T | null {
  const live = list.filter(usable);
  if (opts.chosenId) {
    const chosen = live.find((e) => e.id === opts.chosenId);
    if (chosen) return chosen;
  }
  if (opts.locationId) {
    const farm = live.find((e) => e.is_default && e.location_id === opts.locationId);
    if (farm) return farm;
  }
  const company = live.find((e) => e.is_default && e.location_id == null);
  if (company) return company;
  const here = live.filter((e) => e.location_id == null || e.location_id === opts.locationId);
  return here.length === 1 ? here[0] : null;
}

/** «Pulverizador Hardi 1000 L» — lo que se lee en el parte y en el 1.3. */
export function equipmentLabel(e: Pick<EquipmentRow, "description" | "brand" | "model">): string {
  const extra = [e.brand, e.model].map((s) => (s ?? "").trim()).filter(Boolean);
  const desc = (e.description ?? "").trim();
  // Si la descripción ya dice la marca o el modelo, no se repite.
  const missing = extra.filter((x) => !desc.toLowerCase().includes(x.toLowerCase()));
  return [desc, ...missing].filter(Boolean).join(" ");
}

/** La copia que se guarda en `spray_logs.equipment_snapshot`. */
export interface EquipmentSnapshot {
  id: string;
  descripcion: string;
  tipo: string;
  marca: string | null;
  modelo: string | null;
  regimen: EquipmentRegime | null;
  roma: string | null;
  censo: string | null;
  adquirida: string | null;
  ultima_inspeccion: string | null;
}

export function equipmentSnapshot(e: EquipmentRow): EquipmentSnapshot {
  const clean = (s: string | null | undefined) => {
    const v = (s ?? "").trim();
    return v ? v : null;
  };
  return {
    id: e.id,
    descripcion: equipmentLabel(e),
    tipo: e.equipment_type,
    marca: clean(e.brand),
    modelo: clean(e.model),
    regimen: equipmentRegime(e.equipment_type),
    roma: clean(e.roma_number),
    censo: clean(e.census_reference),
    adquirida: e.acquired_on ?? null,
    ultima_inspeccion: e.last_inspection_on ?? null,
  };
}
